import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Card, message, Spin, Modal, Descriptions, Typography } from 'antd';
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';
import instance from '@/services/axiosConfig';
import ownerService from '@/services/ownerService';

const PermissionPage = () => {
    const { authenticated } = useAuth();
    const { action } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [authorized, setAuthorized] = useState(false);
    const [params, setParams] = useState({});
    const [auditData, setAuditData] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    useEffect(() => {
        const entries = Object.fromEntries(searchParams.entries());
        setParams(entries);

        if (authenticated) {
            setAuthorized(true);
            fetchAuditData();
        } else {
            navigate('/login');
        }

        setLoading(false);
    }, [authenticated, navigate, searchParams]);

    // Fetch Permission Audit Data
    const fetchAuditData = async () => {
        setAuditLoading(true);
        try {
            const response = await ownerService.getpermissionAudit({
                page: pagination.current,
                limit: pagination.pageSize,
            });
            console.log("responseresponse", response)
            setAuditData(response.data);
            setPagination(prev => ({
                ...prev,
                total: response.pagination.total
            }));
            setAuditLoading(false);
        } catch (error) {
            message.error('Failed to fetch audit data');
            setAuditLoading(false);
        }
    };


    const handleTableChange = (newPagination) => {
        setPagination({
            ...pagination,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        });
    };

    useEffect(() => {
        if (authorized) {
            fetchAuditData();
        }
    }, [pagination.current, pagination.pageSize]);

    // Handle Dynamic Actions
    const handleAction = async () => {
        try {
            let endpoint = '';
            let payload = {};

            switch (action) {
                case 'approve':
                    endpoint = '/mail/updatekey/approve';
                    payload = {
                        key: params.key,
                        imo: params.imo,
                        start: params.start,
                        end: params.end,
                        email: params.email,
                    };
                    break;

                case 'reject':
                    Modal.confirm({
                        title: 'Reject Request?',
                        content: 'Are you sure you want to reject this update key request?',
                        okText: 'Reject',
                        okType: 'danger',
                        cancelText: 'Cancel',
                        onOk: async () => {
                            await instance.get('/mail/updatekey/reject', { params });
                            message.success('❌ Request rejected!');
                        },
                    });
                    return;
                case 'previousupdate':
                    endpoint = '/mail/previousupdate/approve';
                    payload = {
                        imo: params.imo,
                        date: params.date,
                        time: params.time,
                        eventType: params.eventType,
                        email: params.email,
                    };
                    break;
                case 'benchmarkapprove':
                    Modal.confirm({
                        title: 'Approve Benchmark Edit Permission?',
                        content: (
                            <div>
                                <p>You are about to approve edit access for:</p>
                                <p><strong>Benchmark:</strong> {params.benchmarkName}</p>
                                <p><strong>Ship IMO:</strong> {params.imo}</p>
                                <p><strong>Requester:</strong> {decodeURIComponent(params.requesterEmail)}</p>
                                <p className="mt-3">A permission key will be sent to the requester's email.</p>
                            </div>
                        ),
                        okText: 'Approve & Send Key',
                        okType: 'primary',
                        cancelText: 'Cancel',
                        onOk: async () => {
                            try {
                                console.log("ASfsdf", params)
                                const response = await instance.get('/mail/benchmark-permission/approve', {
                                    params: {
                                        key: params.key,
                                        benchmarkID: params.benchmarkID,
                                        benchmarkName: params.benchmarkName,
                                        tablename: params.tablename,
                                        imo: params.imo,
                                        requesterEmail: params.requesterEmail,
                                        selectedCells: params.selectedCells,

                                    }
                                });

                                message.success('✅ Benchmark edit permission approved and key sent!');

                                if (response.data?.permissionKey) {
                                    Modal.info({
                                        title: 'Permission Key Generated',
                                        content: (
                                            <div>
                                                <p>The following key has been sent to {decodeURIComponent(params.requesterEmail)}:</p>
                                                <textarea
                                                    readOnly
                                                    style={{
                                                        width: '100%',
                                                        height: '100px',
                                                        fontSize: '11px',
                                                        fontFamily: 'monospace',
                                                        wordBreak: 'break-all'
                                                    }}
                                                    value={response.data.permissionKey}
                                                />
                                            </div>
                                        ),
                                        width: 600,
                                    });
                                }
                            } catch (error) {
                                message.error('Failed to approve: ' + (error.response?.data?.message || error.message));
                            }
                        },
                    });
                    return;

                case 'benchmarkreject':
                    Modal.confirm({
                        title: 'Reject Benchmark Edit Request?',
                        content: (
                            <div>
                                <p>You are about to reject edit access for:</p>
                                <p><strong>Benchmark:</strong> {params.benchmarkName}</p>
                                <p><strong>Ship IMO:</strong> {params.imo}</p>
                                <p><strong>Requester:</strong> {decodeURIComponent(params.requesterEmail)}</p>
                            </div>
                        ),
                        okText: 'Reject Request',
                        okType: 'danger',
                        cancelText: 'Cancel',
                        onOk: async () => {
                            try {
                                await instance.get('/mail/benchmark-permission/reject', {
                                    params: {
                                        key: params.key,
                                        benchmarkID: params.benchmarkID,
                                        benchmarkName: params.benchmarkName,
                                        imo: params.imo,
                                        requesterEmail: params.requesterEmail,
                                    }
                                });
                                message.success('❌ Benchmark edit request rejected!');
                            } catch (error) {
                                message.error('Failed to reject: ' + (error.response?.data?.message || error.message));
                            }
                        },
                    });
                    return;

                case 'delete':
                    endpoint = '/mail/event/delete';
                    payload = {
                        imo: params.imo,
                        date: params.date,
                        time: params.time,
                        eventType: params.eventType,
                        email: params.email,
                    };
                    break;



                default:
                    message.error('Unknown action');
                    return;
            }

            if (endpoint) {
                await instance.get(endpoint, { params: payload });
                message.success(`✅ ${action.toUpperCase()} completed successfully!`);
                const permissionpayload = {
                    action_type: action,
                    permissioninfo: payload,
                }
                await ownerService.savepermissionAudit(permissionpayload);
                fetchAuditData();
            }
        } catch (err) {
            console.error(err);
            message.error(`Error: ${err.response?.data?.message || err.message}`);
        }
    };

    const getActionLabel = () => {
        if (!action) return { title: 'Permission Audit', button: 'Execute', danger: false, icon: <EyeOutlined /> };

        if (action.toLowerCase().includes('approve')) {
            return {
                title: action === 'benchmarkapprove' ? 'Approve Benchmark Edit Permission' : 'Approve Update Key',
                button: 'Approve Request',
                danger: false,
                icon: <CheckCircleOutlined />
            };
        } else if (action.toLowerCase().includes('reject')) {
            return {
                title: action === 'benchmarkreject' ? ' Reject Benchmark Edit Request' : 'Reject Update Key',
                button: 'Reject Request',
                danger: true,
                icon: <CloseCircleOutlined />
            };
        } else if (action.toLowerCase().includes('delete')) {
            return {
                title: 'Delete Event Record',
                button: 'Delete Event',
                danger: true,
                icon: <DeleteOutlined />
            };
        }
        return { title: 'Permission Action', button: 'Execute', danger: false, icon: <EyeOutlined /> };
    };

    // Table Columns
    const columns = [

        {
            title: 'Permission Type',
            dataIndex: 'action_type',
            key: 'action_type',
            width: 130,
            render: (action) => {
                let color = 'default';
                if (action === 'granted' || action === 'approved') color = 'green';
                if (action === 'revoked' || action === 'rejected') color = 'red';
                return <Tag color={color}>{action.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Owner ID',
            dataIndex: 'owner_id',
            key: 'owner_id',
            width: 120
        },
        {
            title: 'Permission Info',
            dataIndex: 'permissioninfo',
            key: 'permissioninfo',
            width: 200,
            render: (info) => (
                <div className="small text-muted">
                    <div><strong>Module:</strong> {info.module}</div>
                    <div><strong>Level:</strong> {info.level}</div>
                </div>
            )
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp) => new Date(timestamp).toLocaleString()
        }
    ];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Spin size="large" />
            </div>
        );
    }

    if (!authorized) return null;

    const label = getActionLabel();

    return (
        <div className="container-fluid mt-4 mb-4">
            {/* Dynamic Parameters Card - Top Section */}
            {action && Object.keys(params).length > 0 && (
                <Card className="mb-4 shadow-sm">
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">
                                    {label.icon} {label.title}
                                </h5>
                                <Button
                                    type={label.danger ? 'default' : 'primary'}
                                    danger={label.danger}
                                    size="large"
                                    icon={label.icon}
                                    onClick={handleAction}
                                >
                                    {label.button}
                                </Button>
                            </div>

                            <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} size="small">
                                {Object.entries(params).map(([key, value]) => (
                                    <Descriptions.Item
                                        key={key}
                                        label={<strong>{key.replace(/([A-Z])/g, ' $1').toUpperCase()}</strong>}
                                    >
                                        <span style={{ wordBreak: 'break-all' }}>
                                            {decodeURIComponent(value)}
                                        </span>
                                    </Descriptions.Item>
                                ))}
                            </Descriptions>
                        </div>
                    </div>
                </Card>
            )}

            {/* Permission Audit List - Bottom Section */}
            <Card
                title={
                    <div className="d-flex justify-content-between align-items-center">
                        <Typography.Title level={4} className="mb-0">Permission Audit Log</Typography.Title>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchAuditData}
                            loading={auditLoading}
                        >
                            Refresh
                        </Button>
                    </div>
                }
                className="shadow-sm"
            >
                <Table
                    columns={columns}
                    dataSource={auditData}
                    rowKey="permission_id"
                    loading={auditLoading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} records`,
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1000 }}
                />
            </Card>
        </div>
    );
};

export default PermissionPage;